/**
 * Parse LLM text into typed segments for rich rendering.
 *
 * Fault-tolerant: if no patterns match, returns a single text segment
 * and the component renders plain text as before.
 */

interface TextSegment {
  type: 'text';
  content: string;
}

interface StrongsSegment {
  type: 'strongs';
  number: string;
  displayText: string;
}

interface VerseRefSegment {
  type: 'verse-ref';
  reference: string;
  displayText: string;
}

export type MessageSegment = TextSegment | StrongsSegment | VerseRefSegment;

/** Strong's number with optional transliteration: G26, H7225, G26 (agape) */
const STRONGS_PATTERN = /\b([HG]\d{1,5})(?:\s*\([^)]+\))?/gi;

/** Verse reference: John 3:16, 1 John 4:9, Gen 1:1-3.
 *  The leading "<digit><space>" is captured as a unit so the regex
 *  cannot greedily consume a stand-alone leading space (which would eat
 *  the gap before the chip — e.g. "read John 3:16" → "readJohn 3:16"). */
const VERSE_REF_PATTERN = /\b((?:\d\s)?[A-Z][a-z]+\.?\s+\d+:\d+(?:-\d+)?)\b/g;

/** Parse text into ordered segments of text, strongs refs, and verse refs. */
export function parseMessageSegments(text: string): MessageSegment[] {
  if (!text) return [{ type: 'text', content: '' }];

  // Collect all matches with their positions
  interface Match {
    type: 'strongs' | 'verse-ref';
    start: number;
    end: number;
    number?: string;
    displayText: string;
    reference?: string;
  }

  const matches: Match[] = [];

  // Find Strong's numbers
  let m: RegExpExecArray | null;
  STRONGS_PATTERN.lastIndex = 0;
  while ((m = STRONGS_PATTERN.exec(text)) !== null) {
    matches.push({
      type: 'strongs',
      start: m.index,
      end: m.index + m[0].length,
      number: m[1].toUpperCase(),
      displayText: m[0],
    });
  }

  // Find verse references
  VERSE_REF_PATTERN.lastIndex = 0;
  while ((m = VERSE_REF_PATTERN.exec(text)) !== null) {
    // Don't match if this overlaps a Strong's match
    const overlaps = matches.some(
      (existing) => m!.index < existing.end && m!.index + m![0].length > existing.start,
    );
    if (!overlaps) {
      matches.push({
        type: 'verse-ref',
        start: m.index,
        end: m.index + m[0].length,
        reference: m[1],
        displayText: m[0],
      });
    }
  }

  if (matches.length === 0) {
    return [{ type: 'text', content: text }];
  }

  // Sort by position
  matches.sort((a, b) => a.start - b.start);

  // Build segments
  const segments: MessageSegment[] = [];
  let cursor = 0;

  for (const match of matches) {
    if (match.start > cursor) {
      segments.push({ type: 'text', content: text.slice(cursor, match.start) });
    }

    if (match.type === 'strongs') {
      segments.push({ type: 'strongs', number: match.number!, displayText: match.displayText });
    } else {
      segments.push({ type: 'verse-ref', reference: match.reference!, displayText: match.displayText });
    }

    cursor = match.end;
  }

  if (cursor < text.length) {
    segments.push({ type: 'text', content: text.slice(cursor) });
  }

  return segments;
}

export interface ScriptureHeader {
  verseText: string;
  reference: string;
}

/**
 * Split the "It is written..." header from the top of an LLM response.
 * Returns the header parts and the remaining body text.
 *
 * Matches two opener formats:
 *   - Old (V3/V4 era):  It is written... "verse text" — Reference
 *   - New (gold-shape): It is written, "verse text" (Reference).
 */
export function splitScriptureHeader(
  text: string,
): { header: ScriptureHeader; body: string } | null {
  // Try the new format first: It is written, "verse" (Reference)[.]
  const parenPattern = /^It is written[,\s]*["""](.+?)["""]?\s*\(([^)]+)\)\.?\s*(?:\n\n|\n(?=[A-Z\d]))/s;
  const parenMatch = text.match(parenPattern);
  if (parenMatch) {
    return {
      header: { verseText: parenMatch[1].trim(), reference: parenMatch[2].trim() },
      body: text.slice(parenMatch[0].length),
    };
  }

  // Fall back to the old em-dash format: It is written... "verse" — Reference
  const dashPattern = /^It is written\.{0,3}\s*["""](.+?)["""]?\s*[—–-]\s*(.+?)(?:\n\n|\n(?=[A-Z\d]))/s;
  const dashMatch = text.match(dashPattern);
  if (!dashMatch) return null;
  return {
    header: { verseText: dashMatch[1].trim(), reference: dashMatch[2].trim() },
    body: text.slice(dashMatch[0].length),
  };
}
